import { Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSelectChange } from "@angular/material/select";
import moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ColorPalette } from "src/app/classes/color-palette";
import { Coordinates, GeoLocationObject } from "src/app/classes/geo-location-object";
import { Profile } from "src/app/classes/profile";
import { ProfileUpdate } from "src/app/classes/profile-update";
import { Van } from "src/app/classes/van";
import { COLOR_PALETTE, PROFILE_KEY } from "src/app/common/constants";
import { CoordinatesService } from "src/app/services/coordinates.service";
import { DispatchService } from "src/app/services/dispatch.service";
import { EventsDeltaService } from "src/app/services/events-delta.service";
import { LoggerService } from "src/app/services/logger.service";
import { ProfileService } from "src/app/services/profile.service";
import { ToastService } from "src/app/services/toast.service";

export interface DialogData {
  type: string;
}
@Component({
  standalone: false,
  selector: "app-profile",
  templateUrl: "./profile.component.html",
  styleUrls: ["./profile.component.scss"],
})
export class ProfileComponent implements OnInit, OnDestroy {
  constructor(
    private profileService: ProfileService,
    private eventsDeltaService: EventsDeltaService,
    private dispatchService: DispatchService,
    formBuilder: FormBuilder,
    private logger: LoggerService,
    private toastService: ToastService,
    private coordinatesService: CoordinatesService,
    public dialogRef: MatDialogRef<ProfileComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    const loadedProfile = this.loadProfile();
    this.formGroup = formBuilder.group(loadedProfile);
    this.color = this.colors[this.colors.findIndex((x) => x.name === loadedProfile.vanBgColorPref['name'])];
    this.color1 =this.colors[this.colors.findIndex((x) => x.name === loadedProfile.vanFontColorPref['name'])];
    this.formGroup.get('vanBgColorPref').setValue(this.color);
    this.formGroup.get('vanFontColorPref').setValue(this.color1);
  }

  @ViewChild('vanPreview', { static: true }) vanPreviewElement: ElementRef;
  formGroup: FormGroup;
  placeholder = 'Van Color';
  @Output() selected = new EventEmitter<ColorPalette>();

  placeholder1 = 'Font Color';
  @Output() selected1 = new EventEmitter<ColorPalette>();

  color: ColorPalette;
  color1: ColorPalette;
  colors = COLOR_PALETTE;

  profile: Profile = new Profile();
  vanList: Array<Van> = new Array<Van>();
  unsubscribe$: Subject<boolean> = new Subject();
  profileUpdate: ProfileUpdate = this.loadProfile();
  @Output() hidePanel: EventEmitter<any> = new EventEmitter();
  location: GeoLocationObject = new GeoLocationObject();
  vanImage:any = '';
  formErrorMsg: string = ''
  isDialog = false;
  initials = '';

  ngOnInit(): void {
    // console.log(this.profileUpdate);
    this.subscribeToProfile();
    this.updateVanImage();
    this.subscribeToCoordinates();
    this.isDialog = !this.isEmptyObject(this.data);
  }

  subscribeToProfile(): void{
    this.profileService.getProfileObs()
      .pipe(
        takeUntil(this.unsubscribe$))
        .subscribe((profile) => {
          if (profile) {
            this.profile = profile;
            this.vanList = profile.vanList;
          }
      });
  }

  ngOnDestroy():void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  isEmptyObject(obj) {
    return (obj && (Object.keys(obj).length === 0));
 }

 subscribeToCoordinates(): void{
  this.coordinatesService.coordinates$.subscribe((coordinates: Coordinates) => {
    if (coordinates && coordinates.latitude && coordinates.longitude) {
      this.location.latitude = coordinates.latitude;
      this.location.longitude = coordinates.longitude;
    }
  });
}

  saveProfile(){
    if(this.validateForm()){
      const profileUpdateFormValues: ProfileUpdate = this.formGroup.value;
      profileUpdateFormValues.latitude = this.location.latitude.toString();
      profileUpdateFormValues.longitude = this.location.longitude.toString();
      profileUpdateFormValues.driverName = this.makeAccountName(this.profile);
      profileUpdateFormValues.gemsid = this.profile.adId;
      profileUpdateFormValues.breakTime = profileUpdateFormValues.onBreak ? moment().format('HH:mm') : '';
      this.profileService.saveProfile(profileUpdateFormValues)
      .subscribe({
        next: resp => {
          this.logger.debug(`PUT Asset response: ${resp.status} ${resp.statusText}`, resp, 'http');
          if(resp.status === 201){
            this.toastService.show('Profile Saved Successfully!', { classname: 'bg-success text-light', delay: 4000 });
            this.logger.debug('saving profile update to local storage', 'ui');
            this.saveProfileToLocalStorage(profileUpdateFormValues);
            setTimeout(() => {
              this.eventsDeltaService.getDeltas();
              this.dispatchService.runWorkerThread();
              this.isDialog ? this.dialogRef.close() : this.cancel();
            }, 650);
          }else{
            this.toastService.show('Error Saving Profile!', { classname: 'bg-danger text-light', delay: 4000 });
          }
        },
        error: err => {
          //Bug 7093024: van already actively assigned to another driver
          if (err && err.status === 409) {
            const body = err.error || {};
            const who = body.currentDriverName || body.currentDriverId || 'another driver';
            const van = body.vanName || 'that van';
            this.toastService.show(
              `Van ${van} is already assigned to ${who}. Please choose a different van.`,
              { classname: 'bg-warning text-dark', delay: 8000 }
            );
          } else {
            this.toastService.show('Error Saving Profile!', { classname: 'bg-danger text-light', delay: 4000 });
          }
        }
      });
    }
  }

  saveProfileToLocalStorage(profileUpdate: ProfileUpdate){
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profileUpdate));
  }

  validateForm(){
    const initials = this.formGroup.get('vanDriverInitialsPref').value;
    const vanNum = this.formGroup.get('vanDriverVanNumberPref').value;
    const bgcolor = this.formGroup.get('vanBgColorPref').value;
    const fgcolor = this.formGroup.get('vanFontColorPref').value;

    if(initials.length !== 3){
      this.formErrorMsg = '3-Letter Label is required'
      return false;
    }
    if(vanNum == null || vanNum == '' || vanNum.length > 35 || vanNum.length < 3){
      this.formErrorMsg = 'Van number is invalid'
      return false;
    }
    if(bgcolor['name'] === fgcolor['name']){
      this.formErrorMsg = 'Background & font colors can not be the same!'
      return false;
    }
    this.formErrorMsg = '';
    return true;
  }

  makeAccountName(profile: Profile): string {
    return `${profile.firstName[0].toUpperCase() || ''}. ${profile.lastName || ''}`;
  }

  selectColor(c: ColorPalette) {
    this.color = c;
    this.selected.emit(c);
    this.updateVanImage();
  }

  updateColor(event: MatSelectChange) {
    this.selectColor(event.value);
  }

  selectColor1(c: ColorPalette) {
    this.color1 = c;
    this.selected1.emit(c);
    this.updateVanImage();
  }

  updateColor1(event: MatSelectChange) {
    this.selectColor1(event.value);
  }

  loadProfile(): ProfileUpdate{
    const localStorageString = localStorage.getItem(PROFILE_KEY);
    return localStorageString !== null ? JSON.parse(localStorageString) : new ProfileUpdate();
  }

  cancel(): void{
    this.hidePanel.emit('hide');
  }

  cancelProfileUpdate(){
    if(this.isDialog){
      this.dialogRef.close();
    }else{
      this.cancel();
    }
  }

  updateVanImage(){
    this.initials = this.formGroup.get('vanDriverInitialsPref').value;
  }

  keyUpUpperCase(){
    this.formGroup.get('vanDriverInitialsPref').setValue(this.formGroup.get('vanDriverInitialsPref').value.toUpperCase());
  }

  keyPressAlphaNumeric(event) {
    const inp = String.fromCharCode(event.keyCode);
    if (/[a-zA-Z]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

}
